/* eslint-disable @typescript-eslint/camelcase */
import React, { useEffect, ComponentType } from 'react';
import dayjs from 'dayjs';
import Spacer from 'components/UI/Layout/Spacer';
import Button from 'components/UI/Button/Button';
import Divider, { VerticalDivider } from 'components/UI/Divider';
import { Svg } from 'components/UI/Svg';
import { AuthLiteHeader } from 'components/AuthLite';
import jsonwebtoken from 'jsonwebtoken';
import { oc } from 'ts-optchain';
import { connect, useDispatch } from 'react-redux';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import Encryption from 'services/Encryption';
import {
  authLiteDownloadLabDetails,
  getAuthLiteTestResultDetails,
  authLiteDownloadPatientLetter
} from 'store/testResults/actions';
import * as testResults from 'store/testResults/selectors';
import * as appSettingsSelectors from 'store/appSettings/selectors';
import * as routeSelectors from 'store/router/selectors';
import { RootState, RootDispatch } from 'store/types';
import {
  TestResult,
  LabResult,
  LabResultsListItems,
  LabDetails,
  PatientLetters
} from 'store/testResults/types';
import Typography from 'components/UI/Typography';
import { Color } from 'modules/styles/colors';
import { FontSize, FontWeight, IconSize } from 'modules/styles/variables';
import Box from 'components/UI/Layout/Box';
import FlexBox, { FlexBoxRow } from 'components/UI/Layout/FlexBox';
import Spinner from 'components/UI/Spinner/Spinner';
import { getLabValue, orNA } from '../../TestResults/Lab/utils';

import {
  MuiBox,
  MuiPaper,
  MuiTypography,
  MuiTable,
  MuiTableRow,
  MuiTableHead,
  MuiTableBody,
  MuiButton,
  CursorMuiBox
} from 'theme/material-ui';
import { TableCell, FooterBanner, DownloadToast, StyledText, IconText } from '../styled';
import { formatPhone } from 'services/formatPhone';
import { authLiteDataSelector } from 'store/authentication/selectors';
import { AuthLiteResponse } from 'store/authentication/types';
import { LanguageButton, DownloadButton } from './styled';
import { History } from 'history';
import { patchAuthLiteEventBySourceId } from 'store/events/actions';
import {
  labDetailsDownloadingSelector,
  patientLetterDownloadingSelector,
  labDetailsDownloadDataSelector,
  patientLetterDownloadDataSelector
} from 'store/testResults/selectors';
import { isIOS } from 'lib/browser';
import { MY_HEALTH_PLUS_DOWNLOAD_SPLASH } from '../constants';
import LinkingServices from 'services/LinkingServices';
import { technicalHelpNumber } from 'modules/constants/phoneNumbers';
import { cleanPhone } from 'modules/utils/PhoneUtils';

// LAB RESULTS
interface LabResultsProps {
  content: LabResultsListItems[];
  isSpanish: boolean;
}
const LabResults = (props: LabResultsProps) => {
  const { content, isSpanish } = props;

  return (
    <>
      <Divider />
      <MuiBox p={2} bgcolor={Color.grayLight3}>
        <Typography fontSize={FontSize.large} fontWeight={FontWeight.bold}>
          Your results
        </Typography>
      </MuiBox>
      <Divider />

      <MuiBox component={MuiPaper} p={3}>
        <MuiTable aria-label="Labs Details">
          <MuiTableHead>
            <MuiTableRow>
              <TableCell id="table-name">Name</TableCell>
              <TableCell id="table-value" align="right">
                Value
              </TableCell>
              <TableCell id="table-interpretation">Interpretation</TableCell>
              <TableCell id="table-more-link">More</TableCell>
            </MuiTableRow>
          </MuiTableHead>
          <MuiTableBody>
            {content.map((item: any) => (
              <>
                <MuiTableRow key={item.name} hover>
                  <TableCell id={item.eventId} component="th" scope="row">
                    {item.name}
                  </TableCell>
                  <TableCell headers={item.eventId} align="right">
                    {getLabValue(item.valueQuantity, item.valueString)}
                  </TableCell>
                  <TableCell headers={item.eventId}>{orNA(item.interpretation)}</TableCell>
                </MuiTableRow>
                <MuiTableRow>
                  <TableCell headers={item.eventId} colSpan={4}>
                    Comments: {isSpanish ? orNA(item.notes_es) : orNA(item.notes)}
                  </TableCell>
                </MuiTableRow>
              </>
            ))}
          </MuiTableBody>
        </MuiTable>
      </MuiBox>
    </>
  );
};

// LAB COMMENTS
export const LabComments = ({ content }: { content: LabResult[] }) => {
  const comments = content.reduce((acc, { notes }) => {
    return notes ? acc.concat(`${notes}<br><br>`) : acc;
  }, '');

  const html = comments.length ? comments : 'No comments';

  return (
    <>
      <Divider />
      <MuiBox p={2} bgcolor={Color.grayLight3}>
        <Typography fontSize={FontSize.large} fontWeight={FontWeight.bold}>
          Additional comments
        </Typography>
      </MuiBox>
      <Divider />

      <Box flex={1} spacing={30} dangerouslySetInnerHTML={{ __html: html }} />
    </>
  );
};

// LAB DETAILS
export const LabDetailSection = ({ content }: { content: LabResult[] }) => {
  const result = content[0];
  return (
    <>
      <Divider />
      <MuiBox p={2} bgcolor={Color.grayLight3}>
        <Typography fontSize={FontSize.large} fontWeight={FontWeight.bold}>
          Lab Details
        </Typography>
      </MuiBox>
      <Divider />

      <MuiBox component={MuiPaper} p={3}>
        <MuiTypography fontWeight={FontWeight.bold}>
          Test Manufacturer:{' '}
          <MuiTypography display="inline">{oc(result).name('N/A')}</MuiTypography>
        </MuiTypography>
        <MuiTypography fontWeight={FontWeight.bold}>
          Specimen Source:{' '}
          <MuiTypography display="inline">{oc(result).specimenSource('N/A')}</MuiTypography>
        </MuiTypography>
        <MuiTypography fontWeight={FontWeight.bold}>
          Collection Date/Time:{' '}
          <MuiTypography display="inline">
            {result?.collectionDate
              ? dayjs(result?.collectionDate).format('MMM DD, YYYY [at] h:mm A')
              : 'N/A'}
          </MuiTypography>
        </MuiTypography>
        <MuiTypography fontWeight={FontWeight.bold}>
          Performing Lab:{' '}
          <MuiTypography display="inline">{oc(result).performingLab('N/A')}</MuiTypography>
        </MuiTypography>
        <MuiTypography fontWeight={FontWeight.bold}>
          Performing Lab Address:{' '}
          <MuiTypography display="inline">{oc(result).performingLabAddress('N/A')}</MuiTypography>
        </MuiTypography>
        <MuiTypography fontWeight={FontWeight.bold}>
          Performing Lab Contact:{' '}
          <MuiTypography display="inline">
            {result?.performingLabPhone ? formatPhone(result?.performingLabPhone) : 'N/A'}
          </MuiTypography>
        </MuiTypography>
      </MuiBox>
    </>
  );
};

// RELATED DOCUMENTS
export const RelatedDocuments = ({
  content,
  downloadPatientLetter,
  patientLetterLoading,
  referringUrl
}: {
  content: LabResult[];
  downloadPatientLetter: (documentId: string) => void;
  patientLetterLoading: boolean;
  referringUrl: string;
}) => {
  // const history = useHistory();
  const handleViewPatientLetters = (item: PatientLetters) => {
    const evnentData: AmplitudeEventData = {
      currentUrl: '/al/test-results',
      referringUrl,
      authentication_type: 'Light'
    };
    analyticsService.logEvent(AnalyticsEvent.DownloadPatientLetterClicked, evnentData);

    // TEMP: DFD-20508 - removing view and adding download feature to patient letters
    // history.push(`/al/test-results/patient-letters/XR-${item.docId}`);
    downloadPatientLetter(`XR-${item.docId}`);
  };
  if (!content) return null;
  const { patientLetters } = content[0];

  if (patientLetters?.some(a => a.docId === 0)) return null;
  if (patientLetters?.length) {
    return (
      <>
        <Divider />
        <MuiBox p={2} bgcolor={Color.grayLight3}>
          <Typography fontSize={FontSize.large} fontWeight={FontWeight.bold}>
            Related Documents
          </Typography>
        </MuiBox>
        <Divider />
        <MuiBox p={3}>
          {patientLetters?.length
            ? patientLetters.map((eachDoc: PatientLetters) => (
                <MuiButton
                  variant="text"
                  color="primary"
                  onClick={() => handleViewPatientLetters(eachDoc)}
                >
                  {patientLetterLoading ? <Spinner /> : `Download ${eachDoc.title}`}
                </MuiButton>
              ))
            : null}
        </MuiBox>
      </>
    );
  }
  return null;
};

// LOADING
export const Loading = () => (
  <FlexBox flex={1} alignItems="center" justifyContent="center" accessibilityLabel="Lab loading">
    <Spinner />
  </FlexBox>
);

// ERROR
export const Error = ({ message }: { message: string }) => (
  <FlexBox flex={1} alignItems="center" justifyContent="center">
    <Typography>{message}</Typography>
  </FlexBox>
);

interface UserData {
  firstName: string;
  lastName: string;
  gender: string;
  dateOfBirth: string;
}

export interface EventTestResultProps {
  loading: boolean;
  error: Error | null;
  data: LabDetails | null;
  isDownloading: boolean;
  labDetailLoading: boolean;
  patientLetterLoading: boolean;
  labDetailData: string;
  patientLetterData: string;
  testResult: TestResult;
  authLiteData: AuthLiteResponse;
  downloadLabs: (testResult: TestResult) => void;
  downloadPatientLetter: (documentId: string) => void;
  history: History;
  currentUrl?: string;
  referringUrl?: string;
  encryptionSettings: {
    password: string;
    algorithm: string;
  };
}

export const EventTestResultComponent = (props: EventTestResultProps) => {
  const {
    loading,
    error,
    data,
    isDownloading,
    authLiteData,
    downloadLabs,
    downloadPatientLetter,
    labDetailLoading,
    patientLetterLoading,
    encryptionSettings,
    referringUrl,
    labDetailData,
    patientLetterData
  } = props;
  const dispatch = useDispatch();
  const [isSpanish, setIsSpanish] = React.useState(false);
  const [userData, setUserData] = React.useState<UserData | null>(null);
  const [showToast, setShowToast] = React.useState(false);

  const content = oc(data).content(undefined) as LabDetails;
  const results = oc(content).results([]);
  const labResults = results.reduce((acc: LabResultsListItems[], { name, source, components }) => {
    const labResultsListItems = components.map(comp => ({
      ...comp,
      source,
      panelName: name
    }));
    return acc.concat(...labResultsListItems);
  }, []);

  const eventUrlData = { currentUrl: '/al/test-results', referringUrl };

  useEffect(() => {
    if (data && results[0]?.name) {
      const eventData: AmplitudeEventData = {
        ...eventUrlData,
        authentication_type: 'Light',
        // we can not pass name to Amplitude as it is PHI
        test_name: Encryption.encrypt(
          encryptionSettings.algorithm,
          encryptionSettings.password,
          results[0].name
        ),
        test_type: 'Lab',
        test_result_displayed: 'Success'
      };
      analyticsService.logEvent(AnalyticsEvent.TestResultsDetailsViewed, eventData);
    }
  }, [data]);

  useEffect(() => {
    if (error && results[0]?.name) {
      const eventData: AmplitudeEventData = {
        ...eventUrlData,
        authentication_type: 'Light',
        // we can not pass name to Amplitude as it is PHI
        test_name: Encryption.encrypt(
          encryptionSettings.algorithm,
          encryptionSettings.password,
          results[0].name
        ),
        test_type: 'Lab',
        test_result_displayed: 'Error',
        test_result_error: error
      };
      analyticsService.logEvent(AnalyticsEvent.TestResultsDetailsViewed, eventData);
    }
  }, [error]);

  useEffect(() => {
    if (authLiteData) {
      const resultJwt = jsonwebtoken.decode(authLiteData.accessToken, { complete: true });
      setUserData(oc(resultJwt).payload.user());

      dispatch(
        getAuthLiteTestResultDetails({
          url: authLiteData.detailsEndpoint
        })
      );
      dispatch(patchAuthLiteEventBySourceId(authLiteData.clinicalEventId, { read: true }));
    }
  }, [authLiteData]);

  useEffect(() => {
    if (Object.values(labDetailData).some(a => a === 'success')) {
      setShowToast(true);
    }
    const timer = setTimeout(() => {
      setShowToast(false);
    }, 3000);
    return () => {
      clearTimeout(timer);
    };
  }, [labDetailData]);

  useEffect(() => {
    if (Object.values(patientLetterData).some(a => a === 'success')) {
      setShowToast(true);
    }
    const timer = setTimeout(() => {
      setShowToast(false);
    }, 3000);
    return () => {
      clearTimeout(timer);
    };
  }, [patientLetterData]);

  const onDownloadPress = () => {
    if (authLiteData) {
      const evnentData: AmplitudeEventData = {
        ...eventUrlData,
        authentication_type: 'Light'
      };
      analyticsService.logEvent(AnalyticsEvent.DownloadTestResultsClicked, evnentData);

      downloadLabs(results[0], authLiteData.detailsEndpoint);
    }
  };

  const openAppDownload = () => {
    const evnentData: AmplitudeEventData = {
      ...eventUrlData,
      promotion_type: 'COVID-19 light authentication: footer'
    };
    analyticsService.logEvent(AnalyticsEvent.PromotionLink, evnentData);

    window.open(MY_HEALTH_PLUS_DOWNLOAD_SPLASH);
  };

  const onSpanishPress = () => {
    const evnentData: AmplitudeEventData = {
      ...eventUrlData,
      language: 'Spanish'
    };
    analyticsService.logEvent(AnalyticsEvent.SwitchLanguage, evnentData);
    setIsSpanish(!isSpanish);
  };

  if (loading) return <Loading />;
  if (error) return <Error message="An error occurred retrieving Lab details" />;
  if (!data) return <Error message="No data available" />;

  return (
    <>
      <MuiBox>
        <AuthLiteHeader />
        <MuiBox p={1} bgcolor={Color.grayHue4} display="flex" justifyContent="center">
          <DownloadButton
            variant="contained"
            color="default"
            onClick={onDownloadPress}
            data-testid="download-button"
            style={{ backgroundColor: Color.white }}
          >
            {labDetailLoading ? (
              <Spinner />
            ) : (
              <>
                <Svg
                  set={isDownloading ? 'downloading' : 'material'}
                  name="get-app"
                  color={Color.black}
                  size={IconSize.base}
                />
                <IconText>Download</IconText>
              </>
            )}
          </DownloadButton>
          <VerticalDivider />
          <LanguageButton
            variant="text"
            color="primary"
            active={isSpanish}
            onClick={() => setIsSpanish(!isSpanish)}
            data-testid="language-change-button"
            disabled={!isSpanish}
          >
            <MuiTypography>English</MuiTypography>
          </LanguageButton>
          <LanguageButton
            variant="text"
            color="primary"
            active={!isSpanish}
            onClick={onSpanishPress}
            data-testid="language-change-button"
            disabled={isSpanish}
          >
            <MuiTypography>Español</MuiTypography>
          </LanguageButton>
        </MuiBox>
        <Divider />
        <MuiBox m={3}>
          <MuiTypography>{`Collection Date/Time: ${dayjs(results[0]?.collectionDate).format(
            'MMM DD, YYYY [at] h:mm A'
          )}`}</MuiTypography>
          {results[0].title ? (
            <MuiTypography variant="h5" fontWeight={FontWeight.semibold}>
              {results[0].title}
            </MuiTypography>
          ) : null}
          <Spacer size="small" />
          <MuiTypography>{`Caregiver: ${oc(results[0]).referringProvider.providerName(
            'N/A'
          )}`}</MuiTypography>
          <MuiTypography>{`Test Location: ${
            results[0].testLocation ? results[0].testLocation : 'N/A'
          }`}</MuiTypography>
          {userData ? (
            <>
              <MuiTypography>{`Patient: ${
                userData ? `${userData.lastName}, ${userData.firstName}` : 'N/A'
              }`}</MuiTypography>
              <MuiTypography>{`DOB: ${userData ? userData.dateOfBirth : 'N/A'}`}</MuiTypography>
              <MuiTypography>{`Gender: ${userData ? userData.gender : 'N/A'}`}</MuiTypography>
            </>
          ) : null}
        </MuiBox>
        <>
          <MuiBox component={MuiPaper}>
            <MuiBox>
              <RelatedDocuments
                referringUrl={referringUrl}
                content={results}
                downloadPatientLetter={downloadPatientLetter}
                patientLetterLoading={patientLetterLoading}
              />
            </MuiBox>
          </MuiBox>
          <MuiBox component={MuiPaper}>
            <MuiBox>
              <LabResults content={labResults} isSpanish={isSpanish} />
            </MuiBox>
          </MuiBox>
          <MuiBox component={MuiPaper}>
            <MuiBox>
              <LabDetailSection content={results} />
            </MuiBox>
          </MuiBox>
          <MuiBox component={MuiPaper}>
            <MuiBox>
              <LabComments content={results} />
            </MuiBox>
          </MuiBox>
        </>
        <Spacer />
      </MuiBox>
      <DownloadToast open={showToast && !isIOS()}>
        <MuiBox m={2} display="flex" flexDirection="row">
          <Svg name="check" set="material" size={IconSize.large} color={Color.white} />
          <StyledText>
            File has downloaded successfully. Please check your downloads folder.
          </StyledText>
        </MuiBox>
      </DownloadToast>
      <FooterBanner>
        <Divider />
        <MuiBox p={2}>
          <FlexBoxRow justifyContent="center">
            <Typography fontSize={FontSize.small} fontWeight={FontWeight.semibold}>
              These results are available for 72 hours. Get My Health+ for long term access to these
              results and all of your Intermountain medical data
            </Typography>
          </FlexBoxRow>
          <Spacer size="small" />
          <FlexBoxRow justifyContent="center">
            <MuiBox width="300px">
              <Button
                variant="web-primary"
                onPress={openAppDownload}
                testID="event-link-expired-mhp-button"
              >
                <MuiBox display="inline-block" paddingBottom="10px">
                  Get
                </MuiBox>
                <MuiBox display="inline-block" top="5px">
                  <Svg
                    name="HeaderMyHealthPlus"
                    accessibilityLabel="MyHealth+ logo"
                    width={100}
                    color="#fff"
                  />
                </MuiBox>
              </Button>
            </MuiBox>
          </FlexBoxRow>
          <MuiBox>
            <Spacer size="small" />
            <MuiTypography fontSize={FontSize.small} display="inline">
              If you need technical support please call{' '}
            </MuiTypography>
            <CursorMuiBox display="inline-block">
              <MuiTypography
                fontSize={FontSize.small}
                color={Color.link}
                onClick={() => LinkingServices.callPhoneNumber(cleanPhone(technicalHelpNumber))}
              >
                {technicalHelpNumber}
              </MuiTypography>
            </CursorMuiBox>
          </MuiBox>
        </MuiBox>
      </FooterBanner>
    </>
  );
};

const mapDispatchToProps = (dispatch: RootDispatch) => ({
  downloadLabs: (testResult: TestResult, detailsEndpoint: string) =>
    dispatch(authLiteDownloadLabDetails(testResult, detailsEndpoint)),
  downloadPatientLetter: (documentId: string) => dispatch(authLiteDownloadPatientLetter(documentId))
});

const mapStateToProps = (state: RootState) => ({
  authLiteData: authLiteDataSelector(state),
  labDetailLoading: labDetailsDownloadingSelector(state),
  labDetailData: labDetailsDownloadDataSelector(state),
  patientLetterLoading: patientLetterDownloadingSelector(state),
  patientLetterData: patientLetterDownloadDataSelector(state),
  loading: testResults.testResultDetailsLoadingSelector(state),
  error: testResults.testResultDetailsErrorSelector(state),
  data: testResults.testResultDetailsDataSelector(state),
  referringUrl: routeSelectors.previousLocationPathNameSelector(state),
  encryptionSettings: appSettingsSelectors.encryptionSettingsSelector(state)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EventTestResultComponent as ComponentType);
